import { api } from "./axios";
import type {
  ChannelCaps,
  EncodeCapsResponse,
  EncodeChannelCapsRaw,
  EncodeStreamCaps,
  EncodeStreamCapsRaw,
} from "../types/encode";

export interface NetworkGlobal {
  default_interface: string;
  hostname: string;
  domain: string;
}

export interface NetworkInterface {
  name: string;
  mac: string;
  dhcp_enable: boolean;
  ip_address: string;
  subnet_mask: string;
  default_gateway: string;
  dns_auto_get: boolean;
  dns_primary: string;
  dns_secondary: string;
  mtu: number;
  type: string;
}

export interface P2PInfo {
  supported: boolean;
  enable: boolean;
  serial: string;
  type: string;
  address: string;
}

export interface NetworkState {
  global: NetworkGlobal;
  interface: NetworkInterface;
  p2p: P2PInfo;
}

export interface NetworkPatchPayload {
  hostname?: string;
  domain?: string;
  dhcp_enable?: boolean;
  ip_address?: string;
  subnet_mask?: string;
  default_gateway?: string;
  mtu?: number;
  dns_auto_get?: boolean;
  dns_primary?: string;
  dns_secondary?: string;
  interface?: string;
}

export async function getNetwork(deviceId: number) {
  const r = await api.get(`/devices/${deviceId}/config/network`);
  return r.data as NetworkState;
}

export async function patchNetwork(deviceId: number, body: NetworkPatchPayload) {
  const r = await api.patch(`/devices/${deviceId}/config/network`, body);
  return r.data as { ok: boolean };
}

export async function patchP2P(deviceId: number, enable: boolean) {
  const r = await api.patch(`/devices/${deviceId}/config/p2p`, { enable });
  return r.data as { ok: boolean };
}

export async function getEncode(deviceId: number) {
  const r = await api.get(`/devices/${deviceId}/config/encode`);
  return r.data;
}

export async function patchEncode(deviceId: number, body: Record<string, string>) {
  const r = await api.patch(`/devices/${deviceId}/config/encode`, body);
  return r.data;
}

export async function getEncodeCaps(deviceId: number, channel: number) {
  const r = await api.get(`/devices/${deviceId}/config/encode-caps`, { params: { channel } });
  return r.data as EncodeCapsResponse;
}

function splitCsv(s: string | undefined): string[] {
  if (!s) return [];
  return s.split(",").map((x) => x.trim()).filter(Boolean);
}

function parseStreamCaps(raw: EncodeStreamCapsRaw | undefined): EncodeStreamCaps | null {
  if (!raw) return null;
  const v = raw.Video ?? {};
  const bro = splitCsv(v.BitRateOptions);
  const min = bro.length > 0 ? Number(bro[0]) : 0;
  const max = bro.length > 1 ? Number(bro[1]) : 0;
  return {
    resolutionTypes: splitCsv(v.ResolutionTypes),
    compressionTypes: splitCsv(v.CompressionTypes),
    bitRateMin: Number.isFinite(min) ? min : 0,
    bitRateMax: Number.isFinite(max) ? max : 0,
    bitRateSuggested: typeof v.BitRateSuggested === "number" ? v.BitRateSuggested : 0,
    fpsMax: typeof v.FPSMax === "number" ? v.FPSMax : 30,
    h264Profiles: Array.isArray(v.H264Profile) ? v.H264Profile : [],
    audioCompressionTypes: splitCsv(raw.Audio?.CompressionTypes),
  };
}

export function parseCapsForChannel(
  resp: EncodeCapsResponse,
  channelIdx0: number,
): ChannelCaps | null {
  const caps: EncodeChannelCapsRaw | undefined = resp.caps?.[channelIdx0];
  if (!caps) return null;
  const range = caps.BitRateRange ?? [0, 0];
  return {
    bitRateRangeMin: Number(range[0]) || 0,
    bitRateRangeMax: Number(range[1]) || 0,
    main: parseStreamCaps(caps.MainFormat?.[0]),
    extra: parseStreamCaps(caps.ExtraFormat?.[0]),
  };
}

export async function getChannelTitles(deviceId: number) {
  const r = await api.get(`/devices/${deviceId}/config/channels`);
  return r.data as { titles: Array<{ channel: number; name: string }> };
}

export async function patchChannelTitles(deviceId: number, titles: Array<{ channel: number; name: string }>) {
  const r = await api.patch(`/devices/${deviceId}/config/channels`, { titles });
  return r.data;
}

export interface DiskInfo {
  index: number;
  name: string;
  state: string;
  state_label: string;
  type: string;
  total_mb: number;
  used_mb: number;
  free_mb: number;
  partitions?: number;
}

export interface StorageBasic {
  OverWrite: boolean;
  FileHoldTime: number;
  PacketType: number;
  PacketLength: number;
  PacketSize: number;
}

export interface RecordModeEntry {
  channel: number;
  Mode: number;
  ModeExtra1: number;
  ModeExtra2: number;
  SnapOn: boolean;
}

export async function getStorage(deviceId: number) {
  const r = await api.get(`/devices/${deviceId}/config/storage`);
  return r.data as {
    disks: DiskInfo[];
    raw: any;
    basic: StorageBasic | Record<string, never>;
    record_mode: RecordModeEntry[];
  };
}

export async function patchStorageBasic(deviceId: number, body: Record<string, string>) {
  const r = await api.patch(`/devices/${deviceId}/config/storage-basic`, body);
  return r.data;
}

export async function patchRecordMode(deviceId: number, body: Record<string, string>) {
  const r = await api.patch(`/devices/${deviceId}/config/record-mode`, body);
  return r.data;
}

export interface DstSection {
  Year: number;
  Month: number;
  Week: number;
  Day: number;
  Hour: number;
  Minute: number;
}

export interface DstConfig {
  Enable: boolean;
  Start: DstSection;
  End: DstSection;
}

export interface NtpConfig {
  Enable: boolean;
  Address: string;
  Port: number;
  UpdatePeriod: number;
}

export interface TimeState {
  current_time: string;
  timezone: number;
  timezone_desc: string;
  dst: DstConfig;
  ntp: NtpConfig;
}

export interface TimePatchPayload {
  time?: string;
  timezone?: number;
  dst?: Partial<DstConfig>;
  ntp?: Partial<NtpConfig>;
}

export async function getTime(deviceId: number) {
  const r = await api.get(`/devices/${deviceId}/config/time`);
  return r.data as TimeState;
}

export async function patchTime(deviceId: number, body: TimePatchPayload) {
  const r = await api.patch(`/devices/${deviceId}/config/time`, body);
  return r.data as { ok: boolean; time?: boolean; config?: boolean; config_keys?: string[]; errors?: string[] };
}

export async function getNtp(deviceId: number) {
  const r = await api.get(`/devices/${deviceId}/config/ntp`);
  return r.data;
}

export async function patchNtp(deviceId: number, body: Record<string, string>) {
  const r = await api.patch(`/devices/${deviceId}/config/ntp`, body);
  return r.data;
}

export async function getMotionDetect(deviceId: number) {
  const r = await api.get(`/devices/${deviceId}/config/motion-detect`);
  return r.data;
}

export async function patchMotionDetect(deviceId: number, body: Record<string, string>) {
  const r = await api.patch(`/devices/${deviceId}/config/motion-detect`, body);
  return r.data;
}

export async function getSnapSchedule(deviceId: number) {
  const r = await api.get(`/devices/${deviceId}/config/snap-schedule`);
  return r.data;
}

export async function patchSnapSchedule(deviceId: number, body: Record<string, string>) {
  const r = await api.patch(`/devices/${deviceId}/config/snap-schedule`, body);
  return r.data;
}

export async function getAutoMaintain(deviceId: number) {
  const r = await api.get(`/devices/${deviceId}/config/auto-maintain`);
  return r.data;
}

export async function patchAutoMaintain(deviceId: number, body: Record<string, string>) {
  const r = await api.patch(`/devices/${deviceId}/config/auto-maintain`, body);
  return r.data;
}

export async function getSmartEvents(deviceId: number) {
  const r = await api.get(`/devices/${deviceId}/config/smart-events`);
  return r.data as {
    smd: { SmartMotionDetect?: any[] } & Record<string, any>;
    ivs_supported: boolean;
    ivs: Record<string, any>;
  };
}

export async function patchSmartEvents(deviceId: number, body: Record<string, string>) {
  const r = await api.patch(`/devices/${deviceId}/config/smart-events`, body);
  return r.data;
}

export async function getChannelSnapshot(deviceId: number, channel: number): Promise<Blob> {
  const r = await api.get(`/devices/${deviceId}/snapshot/${channel}`, { responseType: "blob" });
  return r.data as Blob;
}

export async function getFirmwareInfo(deviceId: number) {
  const r = await api.get(`/devices/${deviceId}/info/firmware`);
  return r.data as {
    model: string | null;
    firmware_db: string | null;
    serial: string | null;
    live: null | {
      model?: string;
      firmware?: string;
      serial?: string;
      vendor?: string;
      machine_name?: string;
      system_info?: Record<string, any>;
      current_time?: string;
    };
    error?: string;
  };
}
